import type { MouseEvent } from 'react';
import { css, etiColors } from '@eti/styles';
import { Card, Inline, LinkButton, vars } from '@etg/wings';
import { useProperty, useTranslation } from '@eti/providers';
import { CaretDown } from '@phosphor-icons/react';
import { m as motion } from 'framer-motion';
import { useToggle } from '../../../../../../common/custom-hooks/toggle';
import { BaggageList } from '../../resultTripCards/components/BaggageList';
import type { BaggageInfo } from '../../resultTripCards/components/TripBaggageInfo';
import BaggageInformationTitle from './BaggageInformationTitle';
import { BaggageInformationBody } from './BaggageInformationBody';

const { signal } = vars.colors;

const cardStyles = (isResultCardBaggageEnabled: boolean, isSeparate: boolean) => css`
  background-color: ${isResultCardBaggageEnabled || isSeparate
    ? `${etiColors.white}`
    : `${signal.information.tint}`};
  border-radius: ${vars.card.borderRadius};
  position: relative;

  ${isResultCardBaggageEnabled &&
  css`
    &::before {
      border-inline-start: 42px solid ${etiColors.white};
      border-start-end-radius: ${vars.card.borderRadius};
      border-top: 42px solid ${signal.information.tint};
      content: '';
      inset-inline-end: 0;
      position: absolute;
      top: 0;
      width: 0;
    }
  `}
`;

interface BaggageInformationProps {
  baggageInfo: BaggageInfo;
  hasLessThan2hSelfTransfer?: boolean;
  dataTestId?: string;
  isResultCardBaggageEnabled?: boolean;
  isSeparate?: boolean;
  isInDrawer?: boolean;
}

const cardSectionStyles = (isOpen: boolean) => css`
  border-bottom: 0 solid ${vars.colors.divider};
  ${isOpen && 'border-width: 1px; transition: border-width 0.2s ease-in'};
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  ${!isOpen && `padding-bottom: 0; transition: padding-bottom 0.6s ease-in`};
`;

const caretDownStyles = (isOpen: boolean) => css`
  transform: rotateX(${isOpen ? '180deg' : '0deg'});
  transition: transform 0.2s ease-in-out;
`;

const motionVariants = {
  show: {
    height: 'auto',
    transitionEnd: { overflow: 'visible' },
  },
  hide: {
    overflow: 'hidden',
    height: 0,
  },
};

const BaggageInformation = ({
  baggageInfo,
  hasLessThan2hSelfTransfer,
  dataTestId = 'baggage-information-wrapper',
  isSeparate = false,
  isResultCardBaggageEnabled = false,
  isInDrawer = false,
}: BaggageInformationProps) => {
  const { p } = useProperty();
  const { t } = useTranslation();
  const isFolded = p('Result.Page.V3.BaggageAccordion.IsFolded');
  const { isOpen, handleToggle } = useToggle(!isFolded);
  const accordionIconAriaLabel = isOpen
    ? t('ScreenReader.Accordion.Collapse.AriaLabel.Text')
    : t('ScreenReader.Accordion.Expand.AriaLabel.Text');

  const accordionClickHandler = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    handleToggle();
  };

  const baggageInformationTitle = isInDrawer ? (
    <Card.Section
      className={cardSectionStyles(isOpen)}
      data-testid="baggage-information-title"
      onClick={handleToggle}
    >
      <BaggageInformationTitle isResultCardBaggageEnabled={isResultCardBaggageEnabled} />
      <Inline alignY="center">
        <BaggageList baggageInfo={baggageInfo} spacing={8} />
        <LinkButton onClick={accordionClickHandler}>
          <CaretDown
            aria-label={accordionIconAriaLabel}
            className={caretDownStyles(isOpen)}
            size={20}
            weight="light"
          />
        </LinkButton>
      </Inline>
    </Card.Section>
  ) : (
    <BaggageInformationTitle isResultCardBaggageEnabled={isResultCardBaggageEnabled} />
  );
  const baggageInformationBody = isInDrawer ? (
    <motion.div
      animate={isOpen ? 'show' : 'hide'}
      aria-hidden={!isOpen}
      data-testid="result-page-baggage-accordion"
      initial={false}
      transition={{ type: 'ease-in-out', duration: 0.4 }}
      variants={motionVariants}
    >
      <BaggageInformationBody
        baggageInfo={baggageInfo}
        hasLessThan2hSelfTransfer={hasLessThan2hSelfTransfer}
        isInDrawer={isInDrawer}
        isResultCardBaggageEnabled={isResultCardBaggageEnabled}
      />
    </motion.div>
  ) : (
    <BaggageInformationBody
      baggageInfo={baggageInfo}
      hasLessThan2hSelfTransfer={hasLessThan2hSelfTransfer}
      isInDrawer={isInDrawer}
      isResultCardBaggageEnabled={isResultCardBaggageEnabled}
    />
  );
  return (
    <Card className={cardStyles(isResultCardBaggageEnabled, isSeparate)} data-testid={dataTestId}>
      {baggageInformationTitle}
      {baggageInformationBody}
    </Card>
  );
};

export default BaggageInformation;
